import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../config/firebase';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import getTheme from '../theme';
// RBAC removed - roleUtils deleted

const defaultColors = {
  primary: '#4d8063',
  secondary: '#1F1F1F',
  sidebar: '',
  topbar: '',
  mainBg: '',
  sidebarText: '',
  topbarText: ''
};

const ThemeContext = createContext({
  isDarkMode: false,
  toggleTheme: () => {},
  primaryColor: defaultColors.primary,
  secondaryColor: defaultColors.secondary,
  sidebarColor: defaultColors.sidebar,
  topbarColor: defaultColors.topbar,
  mainBgColor: defaultColors.mainBg,
  sidebarTextColor: defaultColors.sidebarText,
  topbarTextColor: defaultColors.topbarText,
  setPrimaryColor: () => {},
  setSecondaryColor: () => {},
  setSidebarColor: () => {},
  setTopbarColor: () => {},
  setMainBgColor: () => {},
  setSidebarTextColor: () => {},
  setTopbarTextColor: () => {},
  isAdmin: false,
});

// Add this function at the top of the ThemeProvider component
const clearInvalidThemeData = () => {
  try {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === null) return;
    
    // Try parsing it to validate
    JSON.parse(savedTheme);
  } catch (error) {
    // If parsing fails, remove the invalid data
    localStorage.removeItem('theme');
    console.warn('Cleared invalid theme data from localStorage');
  }
};

// Helper function to convert hex to HSL for CSS variables
const hexToHSL = (hex) => {
  // Remove the # if present
  hex = hex.replace('#', '');
  
  // Convert the hex values to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Find the minimum and maximum values to calculate the lightness
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let l = (max + min) / 2;
  let s = 0;
  let h = 0;
  
  // Calculate the saturation
  if (max !== min) {
    s = l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
    
    // Calculate the hue
    if (max === r) {
      h = (g - b) / (max - min) + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / (max - min) + 2;
    } else if (max === b) {
      h = (r - g) / (max - min) + 4;
    }
    h = h * 60;
  }
  
  // Round the values
  h = Math.round(h);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return { h, s, l };
};

export function ThemeProvider({ children }) {
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialize theme state with a default value
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Handle localStorage initialization in a separate effect
  useEffect(() => {
    const initializeTheme = () => {
      try {
        const savedTheme = localStorage.getItem('theme');
        
        // If no theme is saved, set default and return
        if (!savedTheme) {
          localStorage.setItem('theme', JSON.stringify(false));
          return;
        }

        // Try to parse the saved theme
        const parsed = JSON.parse(savedTheme);
        
        // Validate the parsed value
        if (typeof parsed !== 'boolean') {
          localStorage.setItem('theme', JSON.stringify(false));
          return;
        }

        // Only update state if the parsed value is different
        if (parsed !== isDarkMode) {
          setIsDarkMode(parsed);
        }
      } catch (error) {
        // If there's any error, reset to default
        console.warn('Error initializing theme:', error);
        localStorage.setItem('theme', JSON.stringify(false));
      }
    };

    initializeTheme();
  }, []); // Run once on mount

  const [primaryColor, setPrimaryColorState] = useState(defaultColors.primary);
  const [secondaryColor, setSecondaryColorState] = useState(defaultColors.secondary);
  const [sidebarColor, setSidebarColorState] = useState(defaultColors.sidebar);
  const [topbarColor, setTopbarColorState] = useState(defaultColors.topbar);
  const [mainBgColor, setMainBgColorState] = useState(defaultColors.mainBg);
  const [sidebarTextColor, setSidebarTextColorState] = useState(defaultColors.sidebarText);
  const [topbarTextColor, setTopbarTextColorState] = useState(defaultColors.topbarText);

  const saveBrandColors = useCallback(async (updates) => {
    try {
      await setDoc(doc(db, 'settings', 'company'), updates, { merge: true });
    } catch (error) {
      console.error('Error saving brand colors to Firestore:', error);
    }
  }, []);

  const setPrimaryColor = useCallback((color) => {
    setPrimaryColorState(color);
    saveBrandColors({ primaryColor: color });
  }, [saveBrandColors]);

  const setSecondaryColor = useCallback((color) => {
    setSecondaryColorState(color);
    saveBrandColors({ secondaryColor: color });
  }, [saveBrandColors]);

  const setSidebarColor = useCallback((color) => {
    setSidebarColorState(color);
    saveBrandColors({ sidebarColor: color });
  }, [saveBrandColors]);

  const setTopbarColor = useCallback((color) => {
    setTopbarColorState(color);
    saveBrandColors({ topbarColor: color });
  }, [saveBrandColors]);

  const setMainBgColor = useCallback((color) => {
    setMainBgColorState(color);
    saveBrandColors({ mainBgColor: color });
  }, [saveBrandColors]);

  const setSidebarTextColor = useCallback((color) => {
    setSidebarTextColorState(color);
    saveBrandColors({ sidebarTextColor: color });
  }, [saveBrandColors]);

  const setTopbarTextColor = useCallback((color) => {
    setTopbarTextColorState(color);
    saveBrandColors({ topbarTextColor: color });
  }, [saveBrandColors]);

  // RBAC removed - all authenticated users are treated as admin
  useEffect(() => {
    setIsAdmin(!!auth?.userRole);
  }, [auth?.userRole]);

  // Load global brand colors for everyone (staff, tenant portal, contractor portal).
  // Tenant/contractor often have no Firebase auth user — they still need company colours.
  // Requires Firestore: allow read on settings/company for unauthenticated users (see firestore.rules).
  useEffect(() => {
    let isMounted = true;

    const loadGlobalBrandColors = async () => {
      try {
        const companySettingsRef = doc(db, 'settings', 'company');
        const companySettingsDoc = await getDoc(companySettingsRef);

        if (!isMounted) {
          return;
        }

        if (companySettingsDoc.exists()) {
          const settings = companySettingsDoc.data();

          if (settings.primaryColor) {
            setPrimaryColorState(settings.primaryColor);
          }
          if (settings.secondaryColor) {
            setSecondaryColorState(settings.secondaryColor);
          }
          setSidebarColorState(settings.sidebarColor || defaultColors.sidebar);
          setTopbarColorState(settings.topbarColor || defaultColors.topbar);
          setMainBgColorState(settings.mainBgColor || defaultColors.mainBg);
          setSidebarTextColorState(settings.sidebarTextColor || defaultColors.sidebarText);
          setTopbarTextColorState(settings.topbarTextColor || defaultColors.topbarText);
        }
      } catch (error) {
        if (error.code === 'permission-denied') {
          console.warn('Permission denied reading settings/company (brand colours).');
          return;
        }
        console.error('Error loading global brand colors:', error);
      }
    };

    loadGlobalBrandColors();

    const companySettingsRef = doc(db, 'settings', 'company');
    const unsubscribe = onSnapshot(companySettingsRef, (docSnapshot) => {
      if (!isMounted) {
        return;
      }

      if (docSnapshot.exists()) {
        const settings = docSnapshot.data();

        if (settings.primaryColor) {
          setPrimaryColorState(settings.primaryColor);
        }
        if (settings.secondaryColor) {
          setSecondaryColorState(settings.secondaryColor);
        }
        setSidebarColorState(settings.sidebarColor || defaultColors.sidebar);
        setTopbarColorState(settings.topbarColor || defaultColors.topbar);
        setMainBgColorState(settings.mainBgColor || defaultColors.mainBg);
        setSidebarTextColorState(settings.sidebarTextColor || defaultColors.sidebarText);
        setTopbarTextColorState(settings.topbarTextColor || defaultColors.topbarText);
      }
    }, (error) => {
      if (error.code === 'permission-denied') {
        console.warn('Brand colour listener: permission denied on settings/company');
        return;
      }
      console.error('Error in brand colors listener:', error);
      // Don't reset colors on listener error
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Load user preferences (dark mode only - personal preference)
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (!currentUser) return;
      
      try {
        const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
        const userPrefsDoc = await getDoc(userPrefsRef);
        
        if (userPrefsDoc.exists()) {
          const prefs = userPrefsDoc.data();
          // Only load dark mode preference (brand colors are global)
          setIsDarkMode(prefs?.theme?.isDarkMode ?? false);
        } else {
          // Create default preferences (only dark mode, no brand colors)
          await setDoc(userPrefsRef, {
            theme: {
              isDarkMode: false,
            }
          });
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      }
    };

    loadUserPreferences();
  }, [currentUser]);

  // Save user preferences (dark mode only - brand colors are global)
  const saveUserPreferences = async () => {
    if (!currentUser) return;

    try {
      const db = getFirebaseDb();
      const userPrefsRef = doc(db, 'userPreferences', currentUser.uid);
      
      await updateDoc(userPrefsRef, {
        theme: {
          isDarkMode,
        }
      });
    } catch (error) {
      console.error('Error saving user preferences:', error);
    }
  };

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Save preferences when dark mode changes (brand colors are global, not saved here)
  useEffect(() => {
    if (currentUser) {
      // Debounce saving preferences to avoid multiple rapid DB writes
      const timeoutId = setTimeout(() => {
        saveUserPreferences();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isDarkMode, currentUser]);

  // Update localStorage when theme changes
  useEffect(() => {
    try {
      localStorage.setItem('theme', JSON.stringify(isDarkMode));
    } catch (error) {
      console.warn('Error saving theme:', error);
    }
  }, [isDarkMode]);

  // Apply theme to root element and update CSS variables
  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;

    if (isDarkMode) {
      root.classList.add('dark');
      body.style.backgroundColor = mainBgColor || '#0F172A';
      body.style.color = '#FFFFFF';
    } else {
      root.classList.remove('dark');
      body.style.backgroundColor = mainBgColor || '#FFFFFF';
      body.style.color = '#1F1F1F';
    }

    // Update CSS variables
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--primary-color', primaryColor);
    root.style.setProperty('--color-secondary', secondaryColor);
    root.style.setProperty('--secondary-color', secondaryColor);
    root.style.setProperty('--custom-sidebar-color', sidebarColor || '');
    root.style.setProperty('--custom-topbar-color', topbarColor || '');
    root.style.setProperty('--custom-main-bg-color', mainBgColor || '');
    
    // Convert colors to HSL for Tailwind/shadcn compatibility
    try {
      if (primaryColor) {
        const primaryHSL = hexToHSL(primaryColor);
        root.style.setProperty('--primary', `${primaryHSL.h} ${primaryHSL.s}% ${primaryHSL.l}%`);
      }
      
      if (secondaryColor) {
        const secondaryHSL = hexToHSL(secondaryColor);
        root.style.setProperty('--secondary', `${secondaryHSL.h} ${secondaryHSL.s}% ${secondaryHSL.l}%`);
      }
    } catch (e) {
      console.warn('Error converting colors to HSL:', e);
    }
  }, [isDarkMode, primaryColor, secondaryColor, sidebarColor, topbarColor, mainBgColor]);

  // Helper function to adjust color brightness
  const adjustColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => 
      ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2)
    );
  };

  // Add this at the beginning of your ThemeContext component
  useEffect(() => {
    // RBAC removed - theme settings apply to all users
  }, [auth?.userRole]);

  const muiTheme = useMemo(
    () => getTheme(isDarkMode ? 'dark' : 'light', primaryColor),
    [isDarkMode, primaryColor]
  );

  const value = {
    isDarkMode,
    toggleTheme,
    primaryColor,
    secondaryColor,
    sidebarColor,
    topbarColor,
    mainBgColor,
    sidebarTextColor,
    topbarTextColor,
    setPrimaryColor,
    setSecondaryColor,
    setSidebarColor,
    setTopbarColor,
    setMainBgColor,
    setSidebarTextColor,
    setTopbarTextColor,
    isAdmin,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
